import { omit } from "lodash";
import type { ComponentPropsWithRef } from "react";
import { forwardRef, useMemo } from "react";

import { classNames } from "@/react/helpers/twMergeWithCN";
import type { FieldElement as FieldElementType } from "@/react/types/Element";

import { useProfileFieldPrefill } from "../useProfileFieldPrefill";
import { FieldElement } from "./FieldElement";

interface FieldElementBlockMeta {
  elementableId: number;
  elementId: number;
  fieldIndex: number;
  isFirstChild?: boolean;
  position: number;
  profileFieldId?: number | null;
}

export interface FieldElementBlockProps extends ComponentPropsWithRef<"div"> {
  blockInstanceMeta?: Partial<FieldElementBlockMeta>;
  disabled?: boolean;
  elementConfig?: Partial<FieldElementType["attributes"]>;
  fieldDefaultValue?: string;
  fieldType: FieldElementType["attributes"]["field_type"];
  hidden?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export const FieldElementBlock = forwardRef<
  HTMLDivElement,
  FieldElementBlockProps
>(
  (
    {
      label = "",
      placeholder = "",
      required = false,
      hidden = false,
      className,
      blockInstanceMeta = {},
      fieldDefaultValue,
      disabled,
      elementConfig,
      fieldType,
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { context, profileFieldId, rawPrefillValue } =
      useProfileFieldPrefill(blockInstanceMeta);
    const fallbackIndex = useMemo(() => Date.now(), []);
    const prefillValue = profileFieldId ? rawPrefillValue : undefined;

    const element: FieldElementType = {
      id: blockInstanceMeta.elementId ?? 0,
      elementable_id: blockInstanceMeta.elementableId ?? 0,
      position: blockInstanceMeta.position ?? 0,
      type: "field",
      attributes: {
        label,
        placeholder,
        required,
        hidden,
        field_type: fieldType,
        ...elementConfig,
      },
    };

    const shouldFocus =
      "isModal" in context &&
      "initialFocusRef" in context &&
      context.isModal &&
      blockInstanceMeta.fieldIndex === 0;

    const errors =
      "backendErrors" in context ? (context.backendErrors ?? {}) : {};

    const resolvedDefaultValue =
      fieldDefaultValue ??
      (prefillValue && typeof prefillValue === "string"
        ? prefillValue
        : undefined);

    return (
      <div
        ref={ref}
        className={classNames("w-full", className)}
        {...elementProps}
      >
        <FieldElement
          element={element}
          fieldIndex={blockInstanceMeta.fieldIndex ?? fallbackIndex}
          errors={errors}
          isFirstChild={blockInstanceMeta.isFirstChild}
          initialFocusRef={
            shouldFocus && "initialFocusRef" in context
              ? context.initialFocusRef
              : undefined
          }
          defaultValue={resolvedDefaultValue}
          disabled={disabled}
        />
      </div>
    );
  },
);

FieldElementBlock.displayName = "FieldElementBlock";
